import './styles/fonts.css'

import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import i18n from 'i18next'
import { lazy, Suspense, useEffect } from 'react'

import { setError, store, useAppStore } from '@/app.ts'
import { ConnectionBanner } from '@/components/templates/ConnectionBanner.tsx'
import { Error } from '@/components/templates/Errors/Error'
import { ErrorTop } from '@/components/templates/Errors/ErrorTop'
import { FullScreenLoader } from '@/components/templates/FullScreenLoader.tsx'
import { Notification as NotificationComponent } from '@/components/templates/Notification.tsx'
import { SocketProvider } from '@/contexts/SocketContext.tsx'
import { useAuthentication } from '@/contexts/useAuthenticationContext'
import { useSocket } from '@/contexts/useSocketContext.ts'
import { Login } from '@/screens/Login.tsx'

import { useNotificationPermission } from './hooks/useNotificationPermission'
import { useSmartLook } from './hooks/useSmartLook'
import { Clinician } from './state/stateType'

const WidgetsRouter = lazy(() => import('@/router/WidgetsRouter.tsx'))
const MainRouter = lazy(() => import('@/router/Router.tsx'))

const getNewRelicOptions = () => {
  const accountId = import.meta.env.NEWRELIC_ACCOUNT_ID
  const agentId = import.meta.env.NEWRELIC_AGENT_ID
  const applicationId = import.meta.env.NEWRELIC_APPLICATION_ID
  const licenseKey = import.meta.env.NEWRELIC_LICENSE_KEY
  const trustKey = import.meta.env.NEWRELIC_TRUST_KEY
  const sessionReplayEnabled =
    import.meta.env.NEWRELIC_SESSION_REPLAY_ENABLED === 'true'

  if (!accountId || !applicationId || !licenseKey) {
    return null
  }

  const baseInit = {
    ajax: { deny_list: ['bam.eu01.nr-data.net'] },
    browser_consent_mode: { enabled: false },
    distributed_tracing: { enabled: true },
    performance: { capture_measures: true },
    privacy: { cookies_enabled: true },
  }

  const sessionReplayInit = sessionReplayEnabled
    ? {
        session_replay: {
          block_selector: '',
          collect_fonts: true,
          enabled: true,
          error_sampling_rate: 100.0,
          fix_stylesheets: true,
          inline_images: false,
          inline_stylesheet: true,
          mask_all_inputs: true,
          mask_input_options: {},
          mask_text_selector: '*',
          preload: false,
          sampling_rate: 10.0,
        },
      }
    : {}

  return {
    info: {
      applicationID: applicationId,
      beacon: 'bam.eu01.nr-data.net',
      errorBeacon: 'bam.eu01.nr-data.net',
      licenseKey: licenseKey,
      sa: 1,
    },
    init: { ...baseInit, ...sessionReplayInit },
    loader_config: {
      accountID: accountId,
      agentID: agentId,
      applicationID: applicationId,
      licenseKey: licenseKey,
      trustKey: trustKey,
    },
  }
}

// Only initialize New Relic if configuration is present
const newRelicOptions = getNewRelicOptions()
if (newRelicOptions && import.meta.env.MODE !== 'test') {
  new BrowserAgent(newRelicOptions)
}

dayjs.extend(localeData)

const App = () => {
  const { isAuthenticated, isLoading, setIsLoading, token, user } =
    useAuthentication()
  useEffect(() => {
    store.setLoggedInUser(user?.uid)
    return () => store.setLoggedInUser(undefined)
  }, [user?.uid])

  if (window.location.pathname.split('/')[1] === 'widgets') {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <WidgetsRouter />
      </Suspense>
    )
  }

  if (isLoading) {
    return <FullScreenLoader />
  }

  if ((!isAuthenticated && !isLoading) || !token) {
    return <Login setIsLoading={setIsLoading} />
  }

  return (
    <SocketProvider token={token}>
      <Main />
    </SocketProvider>
  )
}

function Main() {
  const state = useAppStore()
  const clinician: Clinician = state.user
  const { isAuthenticated, isLoading } = useAuthentication()
  const { connectionStatus, reconnect } = useSocket()

  useNotificationPermission()
  useSmartLook(clinician)

  useEffect(() => {
    dayjs.locale(i18n.language)
  }, [])

  if (!isLoading && isAuthenticated && state.error === 'AccessDenied') {
    return <Error text="Access Denied" hasLogoutButton />
  }

  // Chat (socket) data is non-critical: once the connection is flagged
  // degraded, stop waiting on it and render the app shell with a banner
  // instead of blocking every screen behind chat forever.
  if (!state.isInitialized && connectionStatus !== 'degraded') {
    return <FullScreenLoader />
  }

  return (
    <>
      {connectionStatus === 'degraded' && (
        <ConnectionBanner onRetry={reconnect} />
      )}
      {state.error && (
        <ErrorTop message={state.error} onClose={() => setError(undefined)} />
      )}
      <Suspense fallback={<FullScreenLoader />}>
        <MainRouter />
      </Suspense>
      <div id="modal-container" />
      {state.notification && (
        <NotificationComponent text={state.notification} />
      )}
    </>
  )
}

export default App
